<template>
    <button @click="createDoc" class="button is-dark">
        <font-awesome-icon :icon="['fas', 'file-arrow-down']" />
        <span class="ml-2 has-text-weight-bold">Download</span>
    </button>
</template>

<script>
import { saveAs } from "file-saver";
import { Paragraph, Packer, Document, Table, TableRow, TableCell, WidthType, TableLayoutType, ImageRun, TextRun, AlignmentType } from "docx";
import Logo from '../../../public/logo.png';

export default {
    computed: {
        payroll() {
            return JSON.parse(sessionStorage.payrollFiltered);
        },
        form() {
            return JSON.parse(sessionStorage.payrollForm);
        },
        initialDate() {
            return new Date(this.form.initialDate).toLocaleDateString('default', { month: 'long', year: 'numeric' });
        },
        finalDate() {
            return new Date(this.form.finalDate).toLocaleDateString('default', { month: 'long', year: 'numeric' });
        },
        citationDate() {
            return new Date(this.form.citationDate).toLocaleDateString();
        },
        subtotal() {
            return this.payroll.payments.reduce((partialSum, payment) => partialSum + this.getPaymentTotalValue(payment), 0);
        },
        totalValue() {
            return this.subtotal * (1 + (this.form.fee / 100));
        },
        accountName() {
            return this.payroll.account.name;
        }
    },
    methods: {
        async createDoc() {
            const doc = new Document({
                creator: "JudCalc",
                description: "Ficha",
                title: "Processo",
                size: '10pt',
                sections: [{
                    children: [
                        // await this.getHeader(),
                        this.createParagraph('PLANILHA DE DÉBITOS JUDICIAIS', '20pt', true),
                        this.createParagraph(`Período: ${this.initialDate} até ${this.finalDate}`),
                        this.createParagraph(`Data de citação: ${this.citationDate}`),
                        this.createTable(),
                        this.createParagraph(`- Data de atualização de valores: ${new Date().toLocaleDateString('default', { month: 'long', year: 'numeric' })}`, '8pt'),
                        this.createParagraph(`- Indexador utilizado: Taxa Referencial (TR) e após 08/12/2021 a Taxa referencial do Sistema Especial de Liquidação e de Custódia SELIC`, '8pt'),
                        this.createParagraph(`- Juros moratórios simples pelo índice de remuneração da caderneta de poupança, contados da data da citação a partir de ${this.form.citationDate}`, '8pt'),
                        this.createParagraph(`- Acréscimo de 0,00% referente a multa.`, '8pt'),
                        this.createParagraph(`- Honorários advocatícios de ${this.form.fee}%`, '8pt'),
                        this.createParagraphRightAligned(`Subtotal ${this.getCurrency(this.subtotal)}`),
                        this.createParagraphRightAligned(`Honorários advocatícios (${this.getCurrency(this.form.fee)}%)`),
                        this.createParagraphRightAligned(`Total Geral (${this.getCurrency(this.totalValue)}%)`, '12pt', true)
                    ]
                }]
            });

            Packer.toBlob(doc).then((blob) => {
                saveAs(blob, `Planilha de débitos judiciais - ${this.accountName}.docx`);
            });
        },
        createParagraph(text, size = '10pt', bold = false) {
            return new Paragraph({
                children: [
                    this.createTextRun(text, size, bold)
                ]
            });
        },
        createParagraphRightAligned(text, size = '10pt', bold = false) {
            return new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                    this.createTextRun(text, size, bold)
                ]
            });
        },
        createTextRun(text, size = '10pt', bold = false) {
            return new TextRun({
                text: text,
                size: size,
                bold: bold,
                font: 'Arial'
            });
        },
        createTableCell(content) {
            return new TableCell({
                children: [this.createParagraph(content)],
            });
        },
        createRow(pay) {
            const paymentValues = this.getPaymentValues(pay);

            return new TableRow(
                {
                    children: [
                        this.createTableCell(paymentValues.data)
                        , this.createTableCell(this.getCurrency(paymentValues.fgts))
                        , this.createTableCell(this.getCurrency(paymentValues.trView))
                        , this.createTableCell(this.getCurrency(paymentValues.selicView))
                        , this.createTableCell(this.getCurrency(paymentValues.poupanca))
                        , this.createTableCell(this.getCurrency(paymentValues.totalValue))
                    ],
                }
            );
        },
        createHeadRow() {
            return new TableRow(
                {
                    tableHeader: true,
                    children: [
                        this.createTableCell('Vencimento')
                        , this.createTableCell('Principal')
                        , this.createTableCell('Correção (TR)')
                        , this.createTableCell('Selic')
                        , this.createTableCell('Juros')
                        , this.createTableCell('Subtotal')
                    ],
                }
            );
        },
        createTable() {
            let rows = [];

            this.payroll.payments.forEach(pay => {
                let row = this.createRow(pay);

                rows.push(row);
            });



            return new Table(
                {
                    rows: [this.createHeadRow(), ...rows],
                    width: {
                        size: '100%',
                        type: WidthType.AUTO
                    }, layout: TableLayoutType.FIXED
                }
            );
        },
        async getHeader() {
            const logo = await fetch(Logo);
            const imageArrayBuffer = await logo.arrayBuffer();
                    return new Paragraph({
                        children: [
                            new ImageRun({
                                data: imageArrayBuffer,
                                transformation: {
                                    width: 335,
                                    height: 144
                                }
                            })
                        ]
                    });
        },
        // calcs
        getPaymentValues(pay) {
            let values =  {
                data: new Date(pay.date).toLocaleDateString('default', { month: 'short', year: 'numeric' }),
                base: pay.value,
                fgts: 0,
                tr: 0,
                selic: 0,
                poupanca: 0,
                totalValue: 0,
                trView: 0,
                selicView: 0
            };

            values.fgts = pay.value * 0.08;
            values.tr = values.fgts * (1 + pay.tr);
            values.selic = values.tr * (1 + pay.selic);
            values.poupanca = values.selic * (pay.poupanca);
            values.totalValue = values.selic * (1 + pay.poupanca);

            values.trView = values.fgts * (pay.tr);
            values.selicView = values.fgts * (pay.selic);

            return values;
        },
        getPaymentTotalValue(payment) {
            const total = (payment.value * 0.08)
            * (payment.tr ? 1 + payment.tr : 1)
            * (payment.selic ? 1 + payment.selic : 1)
            * (payment.poupanca ? 1 + payment.poupanca : 1);

            return total;
        },
        getCurrency(value) {
            if (value === null || isNaN(value)) return '';
            return value.toLocaleString('pt-br',{style: 'currency', currency: 'BRL'});
        }
    }
}
</script>